<script setup lang="ts">
import { computed, ref } from 'vue'

type StyleType = 'default' | 'primary' | 'secondary' | 'muted' | 'sm' | 'lg' | 'rounded'

const props = withDefaults(
  defineProps<{
    /** 이미지 소스 */
    src?: string
    /** 이미지 너비 */
    width?: string | number
    /** 이미지 높이 */
    height?: string | number
    /** 이미지 대체 텍스트 */
    alt?: string
    /** 이미지 스타일 테마 */
    styletype?: StyleType
    /** 에러 메시지 */
    errorText?: string
    /** 플레이스홀더 텍스트 */
    placeholder?: string
    /** 클릭 가능 여부 */
    clickable?: boolean
    /** 추가 CSS 클래스 */
    class?: string
  }>(),
  {
    styletype: 'default',
    alt: '',
    errorText: '',
    placeholder: '이미지를 불러올 수 없습니다',
    clickable: false,
  },
)

const emit = defineEmits<{
  'click': [event: MouseEvent]
  'mouseover': [event: MouseEvent]
  'mouseout': [event: MouseEvent]
}>()

const STYLE_PRESETS: Record<StyleType, { class?: string }> = {
  default: { class: '' },
  primary: { class: 'border-primary border-2' },
  secondary: { class: 'border-secondary border-2' },
  muted: { class: 'border-muted border-2' },
  sm: { class: 'w-8 h-8' },
  lg: { class: 'w-16 h-16' },
  rounded: { class: 'rounded-full' },
}

const isError = ref(false)
const isLoading = ref(false) // 초기 로딩 상태를 false로 변경

const imageStyle = computed(() => {
  const style: Record<string, string> = {}
  
  if (props.width) {
    style.width = typeof props.width === 'number' ? `${props.width}px` : props.width
  }
  
  if (props.height) {
    style.height = typeof props.height === 'number' ? `${props.height}px` : props.height
  }
  
  return style
})

const imageClass = computed(() => {
  const styleKey = props.styletype || 'default'
  const preset = STYLE_PRESETS[styleKey] ?? STYLE_PRESETS.default
  
  const classes = [
    'object-cover',
    styleKey === 'rounded' ? 'rounded-full' : 'rounded-md',
    preset?.class,
    props.clickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : '',
    props.class
  ].filter(Boolean).join(' ')

  return classes
})

const handleClick = (event: MouseEvent) => {
  if (props.clickable) {
    emit('click', event)
  }
}

const handleMouseOver = (event: MouseEvent) => {
  emit('mouseover', event)
}

const handleMouseOut = (event: MouseEvent) => {
  emit('mouseout', event)
}

const handleImageLoad = () => {
  isLoading.value = false
  isError.value = false
}

const handleImageError = () => {
  isLoading.value = false
  isError.value = true
}
</script>

<template>
  <div class="inline-block">
    <!-- 로딩 상태 -->
    <div v-if="isLoading" class="flex items-center justify-center bg-muted rounded-md" :style="imageStyle">
      <div class="text-muted-foreground text-sm">로딩 중...</div>
    </div>
    
    <!-- 에러 상태 -->
    <div v-else-if="isError" class="flex items-center justify-center bg-muted rounded-md text-center" :style="imageStyle">
      <div class="text-muted-foreground text-sm">
        {{ errorText || placeholder }}
      </div>
    </div>
    
    <!-- 이미지 표시 -->
    <img 
      v-else 
      :src="src"
      :alt="alt"
      :style="imageStyle"
      :class="imageClass"
      @load="handleImageLoad" 
      @error="handleImageError"
      @click="handleClick"
      @mouseover="handleMouseOver"
      @mouseout="handleMouseOut"
    />
  </div>
</template>
